import { Checkbox, Grid, Stack, Typography } from '@mui/material';
import { Restricted } from '@procureai/permissions-ui';
import { NegotiationBoundary } from '@procureai/schemas';
import { FormFieldWrapper } from '@procureai/ui-form-mui';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import {
  TsrctInitiationMode,
  TsrctNegotiationMode,
  TsrctNegotiationObjective,
  TsrctQuoteMode,
  TsrctSupplierInteractionMode,
} from '@/generated/gql/graphql';
import { FeatureFlag } from '~/common/types';

import { NegotiationBoundaryList } from './components/NegotationBoundaryList/NegotiationBoundaryList';

export interface NegotiationTermsData {
  lowerDiscountBoundary?: number | null;
  upperDiscountBoundary?: number | null;
  boundaries?: NegotiationBoundary[] | null;
  boundaryAdjustments?: {
    historicNegotiations?: boolean;
    historicTenderData?: boolean;
  } | null;
  options?: {
    initiatedBy?: TsrctInitiationMode | null;
    quoteMode?: TsrctQuoteMode | null;
    negotiationMode?: TsrctNegotiationMode | null;
    supplierInteractionMode?: TsrctSupplierInteractionMode | null;
    negotiationObjective?: TsrctNegotiationObjective | null;
  } | null;
  negotiatedQuote?: {
    totalPriceDiscount?: number | null;
    createdAt: Date;
    currency: string;
    totalValue?: number | null;
    totalDiscountedValue?: number | null;
    discount?: Union.Nullable<number>;
  } | null;
}

export interface NegotiationTermsProps {
  data: NegotiationTermsData;
}

export const NegotiationTerms = ({ data }: NegotiationTermsProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'NegotiationTerms' });
  const { t: tBoundaryAdjustments } = useTranslation('modules', {
    keyPrefix: 'NegotiationBoundaryAdjustmentsForm',
  });
  const { t: tBoundaries } = useTranslation('modules', { keyPrefix: 'NegotiationBoundariesForm' });

  return (
    <Stack direction="column" spacing={3}>
      <FormFieldWrapper fullWidth label={tBoundaries('boundaries.label')} infoText={tBoundaries('boundaries.infoText')}>
        <NegotiationBoundaryList items={data?.boundaries} />
      </FormFieldWrapper>

      {/* FIXME: Proper Checkbox read-only / view-only component in Form Library required */}
      <FormFieldWrapper fullWidth label={tBoundaryAdjustments('boundaryAdjustments.label')}>
        <Grid container size={12} alignItems="center" spacing={1}>
          <Grid size={10}>
            <Typography variant="body2" color="textPrimary">
              {tBoundaryAdjustments('boundaryAdjustments.historicNegotiations.label')}
            </Typography>
          </Grid>
          <Grid container size={2} justifyContent="end">
            <Checkbox checked={data?.boundaryAdjustments?.historicNegotiations} disabled={true} sx={{ height: 20 }} />
          </Grid>
          <Grid size={10}>
            <Typography variant="body2" color="textPrimary">
              {tBoundaryAdjustments('boundaryAdjustments.historicTenderData.label')}
            </Typography>
          </Grid>
          <Grid container size={2} justifyContent="end">
            <Checkbox checked={data?.boundaryAdjustments?.historicTenderData} disabled={true} sx={{ height: 20 }} />
          </Grid>
        </Grid>
      </FormFieldWrapper>
      {/* </Grid> */}

      <FormFieldWrapper fullWidth label={t('options.negotiationMode.label')}>
        <Typography variant="body2" color="textPrimary">
          {t(`options.negotiationMode.${data?.options?.negotiationMode}`)}
        </Typography>
      </FormFieldWrapper>

      <FormFieldWrapper fullWidth label={t('options.quoteMode.label')}>
        <Typography variant="body2" color="textPrimary">
          {t(`options.quoteMode.${data?.options?.quoteMode}`)}
        </Typography>
      </FormFieldWrapper>

      <FormFieldWrapper fullWidth label={t('options.initiatedBy.label')}>
        <Typography variant="body2" color="textPrimary">
          {t(`options.initiatedBy.${data?.options?.initiatedBy}`)}
        </Typography>
      </FormFieldWrapper>

      <FormFieldWrapper fullWidth label={t('options.supplierInteractionMode.label')}>
        <Typography variant="body2" color="textPrimary">
          {t(`options.supplierInteractionMode.${data?.options?.supplierInteractionMode}`)}
        </Typography>
      </FormFieldWrapper>

      <Restricted needs={{ feature: FeatureFlag.ENABLE_NEGOTIATION_OBJECTIVE }}>
        <FormFieldWrapper fullWidth label={t('options.negotiationObjective.label')}>
          <Typography variant="body2" color="textPrimary">
            {t(`options.negotiationObjective.${data?.options?.negotiationObjective}`)}
          </Typography>
        </FormFieldWrapper>
      </Restricted>
    </Stack>
  );
};
